import { CgDocType } from '../ocr/ocrResponseTypes';

/**
 * Grille canonique source → champ du formulaire CG.
 *
 * Issue de l'analyse des slides 13-14 du Manop CTVO SKIPCAR qui définit le code
 * couleur utilisé par les gestionnaires SKIPCAR pour vérifier un dossier :
 *
 *  - 🟢 `system_known`  = info connue du système (base véhicule MiSIV).
 *                         Pré-remplir en confiance haute, aucune friction UX.
 *  - 🟠 `multi_source`  = info vérifiable sur plusieurs documents.
 *                         Fusion multi-sources, badge de confiance, relecture recommandée.
 *  - 🔴 `single_source` = un seul doc officiel fait foi.
 *                         Priorité OCR exclusive à ce doc + **click-through obligatoire**
 *                         par l'utilisateur avant de pouvoir soumettre le formulaire.
 *
 * Cette grille est consommée par :
 *  - `ReviewFormStep` pour colorer les champs et piloter le bouton Soumettre
 *  - `mergeExtractedFields` pour résoudre les priorités de fusion
 */

export type VerificationTier = 'system_known' | 'multi_source' | 'single_source';

export interface FieldSourceConfig {
  canonicalDocs: CgDocType[];          // docs d'origine, priorité décroissante
  verificationTier: VerificationTier;
}

/**
 * Clés de champs utilisées par FormCGIA dans le formulaire final.
 * Ces clés correspondent à celles extraites par les prompts OCR.
 */
export type FieldKey =
  // Véhicule
  | 'immatriculation'
  | 'vin'
  | 'numero_formule'
  | 'date_premiere_mec'
  | 'marque'
  | 'genre'
  | 'energie'
  | 'puissance_cv'
  | 'date_vente'
  | 'heure_vente'
  // Vendeur
  | 'seller_client_type'
  | 'seller_civilite'
  | 'seller_nom'
  | 'seller_prenom'
  | 'seller_nom_usage'
  | 'seller_siren'
  | 'seller_siret'
  | 'seller_raison_sociale'
  // Nouveau titulaire
  | 'buyer_client_type'
  | 'buyer_civilite'
  | 'buyer_nom'
  | 'buyer_prenom'
  | 'buyer_nom_usage'
  | 'buyer_date_naissance'
  | 'buyer_lieu_naissance'
  | 'buyer_departement_naissance'
  | 'buyer_pays_naissance'
  | 'buyer_siret'
  | 'buyer_raison_sociale'
  | 'buyer_numero_voie'
  | 'buyer_extension_voie'
  | 'buyer_type_voie'
  | 'buyer_nom_voie'
  | 'buyer_lieu_dit'
  | 'buyer_etage_escalier'
  | 'buyer_complement_adresse'
  | 'buyer_boite_postale'
  | 'buyer_code_postal'
  | 'buyer_ville'
  | 'buyer_pays'
  | 'buyer_telephone'
  | 'buyer_residence_type'
  // Co-titulaire
  | 'cobuyer_present'
  | 'cobuyer_client_type'
  | 'cobuyer_civilite'
  | 'cobuyer_nom'
  | 'cobuyer_prenom'
  | 'cobuyer_nom_usage'
  | 'cobuyer_siret'
  | 'cobuyer_raison_sociale';

/**
 * Grille source→champ, conforme au Manop.
 */
export const FIELD_SOURCES: Record<FieldKey, FieldSourceConfig> = {
  // ─── Véhicule ────────────────────────────────────────────────────────────
  immatriculation: { canonicalDocs: ['carte_grise'], verificationTier: 'single_source' },
  vin: { canonicalDocs: ['carte_grise', 'cerfa_cession'], verificationTier: 'system_known' },
  numero_formule: { canonicalDocs: ['carte_grise'], verificationTier: 'system_known' },
  date_premiere_mec: { canonicalDocs: ['carte_grise'], verificationTier: 'system_known' },
  marque: { canonicalDocs: ['carte_grise'], verificationTier: 'system_known' },
  genre: { canonicalDocs: ['carte_grise'], verificationTier: 'system_known' },
  energie: { canonicalDocs: ['carte_grise'], verificationTier: 'system_known' },
  puissance_cv: { canonicalDocs: ['carte_grise'], verificationTier: 'system_known' },
  date_vente: { canonicalDocs: ['cerfa_cession', 'carte_grise'], verificationTier: 'multi_source' },
  heure_vente: { canonicalDocs: ['cerfa_cession'], verificationTier: 'multi_source' },

  // ─── Vendeur (ancien titulaire) ──────────────────────────────────────────
  seller_client_type: { canonicalDocs: [], verificationTier: 'system_known' },
  seller_civilite: { canonicalDocs: ['carte_grise', 'cerfa_cession'], verificationTier: 'system_known' },
  seller_nom: { canonicalDocs: ['carte_grise', 'cerfa_cession'], verificationTier: 'system_known' },
  seller_prenom: { canonicalDocs: ['carte_grise', 'cerfa_cession'], verificationTier: 'system_known' },
  seller_nom_usage: { canonicalDocs: ['carte_grise'], verificationTier: 'system_known' },
  seller_siren: { canonicalDocs: ['carte_grise'], verificationTier: 'system_known' },
  // Le Kbis du VENDEUR est désormais une source légitime de ses champs société,
  // à côté de la cession et de la carte grise (dédoublement du slot Kbis).
  seller_siret: { canonicalDocs: ['cerfa_cession', 'kbis_vendeur'], verificationTier: 'system_known' },
  seller_raison_sociale: {
    canonicalDocs: ['carte_grise', 'cerfa_cession', 'kbis_vendeur'],
    verificationTier: 'system_known',
  },

  // ─── Nouveau titulaire (acheteur) ────────────────────────────────────────
  buyer_client_type: { canonicalDocs: [], verificationTier: 'system_known' },
  buyer_civilite: { canonicalDocs: ['piece_identite', 'permis_conduire'], verificationTier: 'multi_source' },
  buyer_nom: { canonicalDocs: ['piece_identite', 'permis_conduire'], verificationTier: 'multi_source' },
  buyer_prenom: { canonicalDocs: ['piece_identite', 'permis_conduire'], verificationTier: 'multi_source' },
  buyer_nom_usage: { canonicalDocs: ['piece_identite'], verificationTier: 'multi_source' },
  buyer_date_naissance: { canonicalDocs: ['piece_identite', 'permis_conduire'], verificationTier: 'multi_source' },
  buyer_lieu_naissance: { canonicalDocs: ['piece_identite', 'permis_conduire'], verificationTier: 'multi_source' },
  buyer_departement_naissance: { canonicalDocs: ['piece_identite', 'permis_conduire'], verificationTier: 'multi_source' },
  buyer_pays_naissance: { canonicalDocs: ['piece_identite', 'permis_conduire'], verificationTier: 'multi_source' },
  // Kbis de l'ACQUÉREUR : `kbis` brut conservé pour les dossiers antérieurs au
  // routage, qui n'ont pas de slot de partie.
  buyer_siret: { canonicalDocs: ['kbis_acheteur', 'kbis'], verificationTier: 'multi_source' },
  buyer_raison_sociale: { canonicalDocs: ['kbis_acheteur', 'kbis'], verificationTier: 'multi_source' },

  // Adresse → justif de domicile uniquement (ROUGE dans le Manop)
  buyer_numero_voie: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_extension_voie: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_type_voie: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_nom_voie: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_lieu_dit: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_etage_escalier: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_complement_adresse: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_boite_postale: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_code_postal: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_ville: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_pays: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_telephone: { canonicalDocs: ['justif_domicile'], verificationTier: 'single_source' },
  buyer_residence_type: { canonicalDocs: [], verificationTier: 'system_known' },

  // ─── Co-titulaire ────────────────────────────────────────────────────────
  cobuyer_present: { canonicalDocs: ['carte_grise'], verificationTier: 'single_source' },
  cobuyer_civilite: { canonicalDocs: ['piece_identite'], verificationTier: 'single_source' }, // PI cotitulaire
  cobuyer_nom: { canonicalDocs: ['piece_identite'], verificationTier: 'single_source' },
  cobuyer_prenom: { canonicalDocs: ['piece_identite'], verificationTier: 'single_source' },
  cobuyer_nom_usage: { canonicalDocs: ['piece_identite'], verificationTier: 'single_source' },
  cobuyer_client_type: { canonicalDocs: [], verificationTier: 'system_known' },
  cobuyer_siret: { canonicalDocs: [], verificationTier: 'system_known' },
  cobuyer_raison_sociale: { canonicalDocs: [], verificationTier: 'system_known' },
};

/**
 * Liste de toutes les clés de champs pour itération.
 */
export const ALL_FIELD_KEYS = Object.keys(FIELD_SOURCES) as FieldKey[];
